import React, { useEffect, useState } from "react";
import { Ticket, Check, Loader2, ShoppingCart } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const PERK_ICON = {
  unlock_all_worlds: "🌍", vip_levels: "👑", bonus_boss: "⚔️", double_xp: "⚡",
  special_skins: "🎨", supporter_badge: "🏅", early_access: "🚀", ad_free: "🚫",
};
const PERK_LABEL = {
  unlock_all_worlds: "Unlock all worlds", vip_levels: "VIP exclusive levels", bonus_boss: "Bonus boss levels",
  double_xp: "Double XP", special_skins: "Special skins", supporter_badge: "Supporter badge",
  early_access: "Early access", ad_free: "Ad-free play",
};

export default function GamePasses({ gameId, isOwner }) {
  const [passes, setPasses] = useState([]);
  const [buying, setBuying] = useState(null);

  const load = () => api.get(`/games/${gameId}/passes`).then((r) => setPasses(r.data.passes.filter((p) => p.active || p.owned))).catch(() => {});

  useEffect(() => {
    load();
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("pass_session");
    const pid = params.get("pass_id");
    if (sid && pid) {
      const u = new URL(window.location.href);
      u.searchParams.delete("pass_session"); u.searchParams.delete("pass_id");
      window.history.replaceState({}, "", u);
      api.get(`/passes/${pid}/checkout/status/${sid}`).then(({ data }) => {
        if (data.owned) toast.success(`🎉 Pass unlocked: ${data.pass_title || "Game pass"}!`);
        load();
      }).catch(() => {});
    } // eslint-disable-next-line
  }, [gameId]);

  const buy = async (p) => {
    setBuying(p.id);
    try {
      const { data } = await api.post(`/passes/${p.id}/checkout`, { origin_url: window.location.origin });
      if (data.url) { window.location.href = data.url; return; }
      if (data.requires_approval) { toast.info(data.message || "Sent for parent approval."); }
      else if (data.granted) { toast.success("Pass unlocked!"); load(); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBuying(null); }
  };

  if (passes.length === 0) return null;

  return (
    <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="game-passes">
      <h3 className="font-display font-bold mb-3 flex items-center gap-2"><Ticket className="w-5 h-5 text-amber-500" /> Game Passes</h3>
      <div className="space-y-3">
        {passes.map((p) => (
          <div key={p.id} className="rounded-xl border border-[hsl(var(--border))] p-3" data-testid={`buy-pass-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-bold truncate">{p.title}</div>
                {p.description && <div className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">{p.description}</div>}
              </div>
              {p.owned ? (
                <Badge owned />
              ) : (
                <Button size="sm" onClick={() => buy(p)} disabled={buying === p.id || isOwner}
                  className="rounded-full bg-amber-500 hover:bg-amber-600 text-white shrink-0" data-testid={`pass-buy-btn-${p.id}`}>
                  {buying === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />} ${p.price.toFixed(2)}
                </Button>
              )}
            </div>
            {p.perks.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {p.perks.map((k) => (
                  <span key={k} className="text-[11px] px-2 py-0.5 rounded-full bg-[hsl(var(--secondary))] flex items-center gap-1" title={PERK_LABEL[k]}>
                    {PERK_ICON[k]} {PERK_LABEL[k]}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const Badge = ({ owned }) => (
  <span className="shrink-0 text-xs font-semibold text-emerald-500 flex items-center gap-1"><Check className="w-4 h-4" /> Owned</span>
);
